import { useState } from 'react';
import { CheckCircle2, Clapperboard, Loader2, Lock, Pencil, PlayCircle, StopCircle, Trash2, UserCog } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { usePermissions } from '@/hooks/usePermissions';
import * as repo from '../data/votingRepository';
import { useVotingMutation } from '../hooks/useVoting';
import type { ElectionState, StateRace } from '../types';
import { RESULT_LABEL, candidateName, sortedResult } from '../utils/rounds';
import { OverrideDialog } from './OverrideDialog';
import { RaceReplayDialog } from './RaceReplayDialog';
import { RoundCountdown } from './RoundCountdown';

const BASE_LABEL = { ELIGIBLE: 'habilitados', VALID: 'votos válidos' } as const;

interface RoundCardProps {
  state: ElectionState;
  race: StateRace;
  fetchedAt: number;
  /** The last round of its position and its winner is waiting for confirmation */
  awaitingConfirmation: boolean;
  onEdit: (race: StateRace) => void;
}

export function RoundCard({ state, race, fetchedAt, awaitingConfirmation, onEdit }: RoundCardProps) {
  const { can } = usePermissions();
  const electionId = state.election.id;
  const [confirm, setConfirm] = useState<'open' | 'close' | 'delete' | 'winner' | null>(null);
  const [showOverride, setShowOverride] = useState(false);
  const [showReplay, setShowReplay] = useState(false);

  // Votes by channel while the round is open (admin): shares the cache of the Estadísticas tab
  const { data: stats } = useQuery({
    queryKey: ['voting', 'stats', electionId],
    queryFn: () => repo.getElectionStats(electionId),
    enabled: race.status === 'OPEN' && can('vote.view'),
    refetchInterval: 4000,
  });
  const channels = stats?.rounds.find((x) => x.race_id === race.id)?.by_channel;
  const cast = race.votes_cast ?? 0;
  const eligibleNow = race.eligible_now ?? 0;
  const turnoutPct = eligibleNow > 0 ? Math.round((cast / eligibleNow) * 100) : 0;
  const lowTurnout = race.threshold_base === 'ELIGIBLE' && eligibleNow > 0 && turnoutPct < 60;

  const open = useVotingMutation(electionId, () => repo.openRace(race.id), { success: 'Ronda abierta' });
  const close = useVotingMutation(electionId, () => repo.closeRace(race.id), { success: 'Ronda cerrada' });
  const del = useVotingMutation(electionId, () => repo.deleteRace(race.id), { success: 'Ronda eliminada' });
  const winner = useVotingMutation(electionId, () => repo.confirmWinner(race.id), { success: 'Ganador confirmado' });

  const names = (race.candidate_ids ?? []).map((id) => candidateName(state, id));
  const rules = `${Number(race.threshold_pct)} % sobre ${BASE_LABEL[race.threshold_base]}${race.allow_blank ? ' · voto en blanco' : ''}${race.duration_minutes ? ` · ${race.duration_minutes} min` : ''}`;

  return (
    <div className="space-y-3 rounded-md border p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className="font-medium">Ronda {race.round_number}</span>
          {race.status === 'DRAFT' && <Badge variant="secondary">Borrador</Badge>}
          {race.status === 'OPEN' && <Badge className="bg-green-600 hover:bg-green-600">Abierta</Badge>}
          {race.status === 'CLOSED' && (
            <Badge variant={race.result === 'WINNER' ? 'default' : 'outline'}>
              {race.result ? RESULT_LABEL[race.result] : 'Cerrada'}
            </Badge>
          )}
          {race.status === 'OPEN' && <RoundCountdown closesAt={race.closes_at} serverNow={state.server_now} fetchedAt={fetchedAt} />}
        </div>

        <div className="flex flex-wrap gap-2">
          {race.status === 'DRAFT' && (
            <>
              {can('election_race.edit') && (
                <>
                  <Button size="sm" variant="outline" onClick={() => onEdit(race)}><Pencil className="mr-1.5 size-4" /> Editar</Button>
                  <Button size="sm" variant="ghost" onClick={() => setConfirm('delete')}><Trash2 className="size-4 text-destructive" /></Button>
                </>
              )}
              {can('election_race.open') && (
                <Button size="sm" onClick={() => setConfirm('open')}><PlayCircle className="mr-1.5 size-4" /> Abrir votación</Button>
              )}
            </>
          )}
          {race.status === 'OPEN' && (
            <>
              {can('vote.override') && (
                <Button size="sm" variant="outline" onClick={() => setShowOverride(true)}><UserCog className="mr-1.5 size-4" /> Corregir voto</Button>
              )}
              {can('election_race.close') && (
                <Button size="sm" variant="destructive" onClick={() => setConfirm('close')}><StopCircle className="mr-1.5 size-4" /> Cerrar votación</Button>
              )}
            </>
          )}
          {race.status === 'CLOSED' && race.result_data && race.result_data.votes_cast > 0 && (
            <Button size="sm" variant="outline" onClick={() => setShowReplay(true)}><Clapperboard className="mr-1.5 size-4" /> Ver carrera</Button>
          )}
          {race.status === 'CLOSED' && awaitingConfirmation && can('election_race.close') && (
            <Button size="sm" onClick={() => setConfirm('winner')}><CheckCircle2 className="mr-1.5 size-4" /> Confirmar ganador</Button>
          )}
        </div>
      </div>

      <p className="text-xs text-muted-foreground">{rules}</p>

      {race.status === 'DRAFT' && (
        <p className="text-sm">
          <span className="text-muted-foreground">Candidatos ({names.length}): </span>
          {names.join(', ')}
        </p>
      )}

      {race.status === 'OPEN' && (
        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span>Votaron <strong>{race.votes_cast ?? 0}</strong> de <strong>{race.eligible_now ?? 0}</strong> habilitados</span>
            <span className="text-muted-foreground">
              {race.eligible_now ? Math.round(((race.votes_cast ?? 0) / race.eligible_now) * 100) : 0} %
            </span>
          </div>
          <Progress value={race.eligible_now ? ((race.votes_cast ?? 0) / race.eligible_now) * 100 : 0} />
          {channels && (
            <p className="text-xs text-muted-foreground">
              Kiosco {channels.KIOSK ?? 0} · teléfono {channels.PUBLIC_LINK ?? 0} · cuenta {channels.ACCOUNT ?? 0}
            </p>
          )}
          {can('vote.view') ? (
            <ul className="space-y-1 pt-1 text-sm">
              <li className="flex items-center gap-1.5 text-xs text-muted-foreground"><Lock className="size-3" /> Conteo en vivo (solo administradores)</li>
              {(race.candidate_ids ?? []).map((id) => (
                <li key={id} className="flex justify-between"><span>{candidateName(state, id)}</span><strong>{race.live_tally?.[id] ?? 0}</strong></li>
              ))}
              {race.allow_blank && (
                <li className="flex justify-between"><span>Voto en blanco</span><strong>{race.live_tally?.blank ?? 0}</strong></li>
              )}
            </ul>
          ) : (
            <p className="text-xs text-muted-foreground">Los votos por candidato se muestran al cerrar la ronda.</p>
          )}
        </div>
      )}

      {race.status === 'CLOSED' && race.result_data && (
        <div className="space-y-2">
          <ul className="space-y-1.5">
            {sortedResult(race).map((c) => (
              <li key={c.candidate_id} className="space-y-0.5">
                <div className="flex justify-between text-sm">
                  <span className={c.candidate_id === race.result_candidate_id ? 'font-semibold' : ''}>{candidateName(state, c.candidate_id)}</span>
                  <span>{c.votes} · {Number(c.pct).toFixed(1)} %</span>
                </div>
                <Progress value={Math.min(100, Number(c.pct))} />
              </li>
            ))}
          </ul>
          <p className="text-xs text-muted-foreground">
            Habilitados al cierre: {race.result_data.eligible} · votaron {race.result_data.votes_cast}
            {race.result_data.blank > 0 && ` (${race.result_data.blank} en blanco)`} · no votaron {race.result_data.non_voters}
            {race.close_reason === 'TIMER' && ' · cerró por tiempo'}
          </p>
        </div>
      )}

      <AlertDialog open={confirm !== null} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirm === 'open' && '¿Abrir la votación?'}
              {confirm === 'close' && '¿Cerrar la votación?'}
              {confirm === 'delete' && '¿Eliminar esta ronda?'}
              {confirm === 'winner' && '¿Confirmar al ganador?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirm === 'open' && 'Desde ese momento los votantes pueden votar y las reglas de la ronda ya no se pueden cambiar.'}
              {confirm === 'close' &&
                `Nadie podrá votar después. Votaron ${cast} de ${eligibleNow} habilitados (${turnoutPct} %). Se calcula el resultado con los habilitados de este momento.${
                  lowTurnout ? ' Atención: participación baja; quienes no votaron cuentan como en blanco y puede que nadie llegue al porcentaje.' : ''
                }`}
              {confirm === 'delete' && 'Se borra la ronda en borrador.'}
              {confirm === 'winner' && 'El cargo queda cubierto y el candidato ya no se ofrece para otros cargos.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                const m = { open, close, delete: del, winner }[confirm as 'open'];
                m.mutate(undefined);
                setConfirm(null);
              }}
            >
              {(open.isPending || close.isPending || del.isPending || winner.isPending) && <Loader2 className="mr-2 size-4 animate-spin" />}
              Continuar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {race.status === 'CLOSED' && race.result_data && (
        <RaceReplayDialog
          open={showReplay}
          onOpenChange={setShowReplay}
          state={state}
          race={race}
          positionTitle={state.positions.find((p) => p.id === race.position_id)?.title ?? ''}
        />
      )}
      {race.status === 'OPEN' && (
        <OverrideDialog open={showOverride} onOpenChange={setShowOverride} state={state} race={race} />
      )}
    </div>
  );
}
